import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  inject,
  signal,
} from "@angular/core";
import { Router, RouterLink, NavigationEnd } from "@angular/router";
import { filter } from "rxjs/operators";
import { Subscription } from "rxjs";
import { AuthService } from "../../services/auth.service";
import { ThemeService } from "../../services/theme.service";
import { HeaderNavService, NavItem } from "../header-nav.service";
import { SwitchIcons } from "../../../shared/icon/switch-icons/switch-icons";
import { NavMenu } from "../../../shared/layout/nav-menu/nav-menu";
import { BfLogo } from "../../../shared/brand/bf-logo/bf-logo";
import { homeRouteFor } from "../../auth/home-route";

/**
 * Header global (SPA). Além do logo, expõe um botão de menu expansível que
 * sobrepõe a página atual com a navegação principal — os mesmos destinos do
 * dashboard, agora acessíveis de qualquer rota e role-aware (via HeaderNavService).
 */
@Component({
  selector: "app-header",
  standalone: true,
  imports: [RouterLink, SwitchIcons, NavMenu, BfLogo],
  template: `
    <!--
      Spec 026: chrome por token — vidro branco + roxo no claro, vidro roxo +
      texto claro no escuro. Era #271C29/50 fixo, uma faixa cinza no claro.
    -->
    <header
      class="w-full h-[var(--bf-header-h)] sticky top-0 z-30 bg-[var(--bf-chrome-bg)] text-[var(--bf-chrome-text)] backdrop-blur-md border-b border-[var(--bf-chrome-border)]"
    >
      <div class="container mx-auto h-full px-6 flex justify-between items-center">
        <a [routerLink]="homeLink()" [attr.aria-label]="isLoggedIn() ? 'Ir para o meu painel' : 'Página inicial'">
          <app-bf-logo size="12" />
        </a>
        <button
          type="button"
          (click)="toggleMenu()"
          [attr.aria-expanded]="menuOpen()"
          aria-haspopup="dialog"
          aria-label="Abrir menu de navegação"
          class="text-[var(--bf-chrome-text)] hover:opacity-70 transition-opacity duration-200 p-1"
        >
          <app-switch-icons icon="menu" size="8" />
        </button>
      </div>
    </header>

    <app-nav-menu
      [open]="menuOpen()"
      [items]="navItems()"
      [isLoggedIn]="isLoggedIn()"
      [dark]="isDark()"
      (select)="onSelect($event)"
      (close)="closeMenu()"
      (auth)="onAuth()"
      (themeToggle)="theme.toggle()"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnDestroy {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly nav = inject(HeaderNavService);
  private readonly routerSubscription: Subscription;
  readonly theme = inject(ThemeService);

  readonly menuOpen = signal(false);
  readonly isLoggedIn = computed(() => this.auth.isLoggedIn());
  readonly isDark = computed(() => this.theme.theme() === "dark");
  /** Logado, o logo leva ao painel do papel; deslogado, à landing (spec 026 §2). */
  readonly homeLink = computed(() => homeRouteFor(this.auth));
  // Recalcula a cada abertura/mudança de auth (a fonte é role-aware).
  readonly navItems = computed<NavItem[]>(() => {
    this.menuOpen();
    this.isLoggedIn();
    return this.nav.items();
  });

  constructor() {
    // Fecha o menu ao concluir qualquer navegação (rede de segurança p/ logo, etc.).
    this.routerSubscription = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.closeMenu());
  }

  toggleMenu(): void {
    this.menuOpen.update((v) => !v);
  }

  closeMenu(): void {
    this.menuOpen.set(false);
  }

  onSelect(item: NavItem): void {
    this.closeMenu();
    const extras = item.openAction
      ? { queryParams: { open: item.openAction } }
      : {};
    this.router.navigate([item.route], extras);
  }

  onAuth(): void {
    this.closeMenu();
    if (this.isLoggedIn()) {
      this.auth.logout();
    } else {
      this.router.navigate(["/login"]);
    }
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
  }
}
